/**
 * Admin schemas for the "Ask Gemini"-style AI chat assistant
 * (2026-08-03, user request).
 *
 * The assistant answers questions about the page the manager/admin is
 * currently looking at (dashboard, conversation, prompt, campaign). The
 * frontend never sends screen content — only a lightweight `pageContext`
 * pointer (which page + which entity id / filters); the backend gathers the
 * real data itself (deterministic queries, no agentic tool-calling) and
 * makes a single `AIServiceLike.chatCompletion` call per question.
 *
 * Chat history is ephemeral (client-side only, never persisted) — the
 * frontend replays it on every call so the model has conversational
 * context; the backend does not store transcripts beyond the audit hash.
 *
 * **Constitution Article IV (No Invention):** scope (4 pages, manager+ RBAC,
 * 5 questions/day) traces to the 2026-08-03 user decisions captured in the
 * feature discussion; no additional pages or capabilities are invented here.
 */
import { Type } from '@sinclair/typebox';
/** Pages the assistant currently supports — see module docstring. */
export const AiAssistantPage = Type.Union([
    Type.Literal('dashboard'),
    Type.Literal('conversation'),
    Type.Literal('prompt'),
    Type.Literal('campaign'),
]);
/**
 * Dashboard context mirrors the filters the KPI page itself uses
 * (`GET /admin/dashboard/kpis` query params) so the assistant reasons about
 * exactly the period/scope the manager is looking at.
 */
export const AiAssistantDashboardContext = Type.Object({
    page: Type.Literal('dashboard'),
    from: Type.String({ format: 'date-time' }),
    to: Type.String({ format: 'date-time' }),
    sellerId: Type.Optional(Type.String({ format: 'uuid' })),
    campaignId: Type.Optional(Type.String({ format: 'uuid' })),
});
export const AiAssistantConversationContext = Type.Object({
    page: Type.Literal('conversation'),
    conversationId: Type.String({ format: 'uuid' }),
});
export const AiAssistantPromptContext = Type.Object({
    page: Type.Literal('prompt'),
    promptId: Type.String({ format: 'uuid' }),
});
export const AiAssistantCampaignContext = Type.Object({
    page: Type.Literal('campaign'),
    campaignId: Type.String({ format: 'uuid' }),
});
export const AiAssistantPageContext = Type.Union([
    AiAssistantDashboardContext,
    AiAssistantConversationContext,
    AiAssistantPromptContext,
    AiAssistantCampaignContext,
]);
/** Hard ceiling on replayed history entries per call — bounds prompt size. */
export const AI_ASSISTANT_MAX_HISTORY = 20;
export const AiAssistantChatHistoryMessage = Type.Object({
    role: Type.Union([Type.Literal('user'), Type.Literal('assistant')]),
    content: Type.String({ minLength: 1, maxLength: 4000 }),
});
/**
 * `POST /admin/ai-assistant/chat` body. `history` is the ephemeral
 * client-held transcript for this widget session — omit/empty on the first
 * question.
 */
export const AiAssistantChatBody = Type.Object({
    pageContext: AiAssistantPageContext,
    question: Type.String({ minLength: 3, maxLength: 1000 }),
    history: Type.Optional(Type.Array(AiAssistantChatHistoryMessage, { maxItems: AI_ASSISTANT_MAX_HISTORY })),
});
export const AiAssistantTokenUsage = Type.Object({
    in: Type.Integer({ minimum: 0 }),
    out: Type.Integer({ minimum: 0 }),
});
/**
 * `POST /admin/ai-assistant/chat` 200 response. `remainingQuestionsToday`
 * lets the widget show a soft warning before the 429 actually hits.
 */
export const AiAssistantChatResponse = Type.Object({
    answer: Type.String({ minLength: 1 }),
    modelUsed: Type.String({ minLength: 1 }),
    tokenUsage: AiAssistantTokenUsage,
    remainingQuestionsToday: Type.Integer({ minimum: 0 }),
});
/**
 * 429 envelope — daily limit reached. `resetAt` is the next São Paulo
 * midnight (ISO 8601), matching the calendar-day window the rate limiter
 * counts against (`audit_log` rows for `ai_assistant.message_sent`).
 */
export const AiAssistantRateLimitError = Type.Object({
    error: Type.Object({
        code: Type.Literal('RATE_LIMITED'),
        message: Type.String(),
        resetAt: Type.String({ format: 'date-time' }),
    }),
});
/** 404 envelope — the entity referenced by `pageContext` no longer exists. */
export const AiAssistantContextNotFoundError = Type.Object({
    error: Type.Object({
        code: Type.Literal('PAGE_CONTEXT_NOT_FOUND'),
        message: Type.String(),
    }),
});
//# sourceMappingURL=ai-assistant.js.map